import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  RootDiv,
  HeaderAppBar,
  HeaderToolbar,
  PositionDivLeft,
  PositionDivRight,
  HeaderLeftDiv,
  HeaderRightComponent,
  LoginButton,
  MekiLogoLink,
  CancelButton,
  SearchBoxDiv,
} from './Header.styles';
import { MekiLogo } from '@components/icons/LogoMeki';
import { useRouter } from 'next/router';
import { getRoute, removeUndefineds } from '@utils';
import { useAuth } from '@hooks/useAuth';
import { useWindowSize } from '@hooks/useWindowSize';
import { DrawerCart } from '@screens/cart/DrawerCart';
import { useFocusSearchBox } from '@hooks/useFocusSearchBox';
import { DrawerMenu } from '@screens/menu';
import Link from 'next/link';
import { SearchBox } from '@components/searchBox';

interface HeaderProps {
  leftComponent?: React.ReactElement;
  className?: string;
  isHeaderCard?: boolean;
  hasSearchBox?: boolean;
}

export function Header({
  leftComponent,
  className,
  isHeaderCard,
  hasSearchBox = false,
}: HeaderProps) {
  const { width, isDesktop } = useWindowSize();
  const { appUser: user } = useAuth();
  const router = useRouter();
  const focusSearchBox = useFocusSearchBox();
  const [hideInfo, setHideInfo] = useState(false);
  const atTopRef = useRef(
    typeof window !== 'undefined' ? window.scrollY === 0 : true,
  );

  useEffect(() => {
    if (width < 650) {
      setHideInfo(true);
    } else {
      setHideInfo(false);
    }
  }, [focusSearchBox.isFocus, width]);

  useEffect(() => {
    const handleScroll = () => {
      const scroll = Math.max(window.scrollY, 0);
      atTopRef.current = scroll === 0;
    };
    document.addEventListener('scroll', handleScroll);
    return () => {
      document.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleClose = useCallback(() => {
    focusSearchBox.onblur();
  }, [focusSearchBox]);

  const position = isHeaderCard ? 'relative' : 'fixed';

  return (
    <>
      <RootDiv className={className}>
        <HeaderAppBar position={position} color="primary" elevation={1}>
          <HeaderToolbar>
            <PositionDivLeft desktop={isDesktop}>
              <HeaderLeftDiv>
                {!isDesktop && focusSearchBox.isFocus ? null : (
                  <>{leftComponent ? leftComponent : <DrawerMenu />}</>
                )}
                {!hideInfo && (
                  <MekiLogoLink href={getRoute('homeLoggedIn').toString()}>
                    <MekiLogo fill={'#000000de'} height="16px" width="auto" />
                  </MekiLogoLink>
                )}
              </HeaderLeftDiv>
            </PositionDivLeft>
            {hasSearchBox && (
              <SearchBoxDiv>
                <SearchBox isHeader={true} isVisible={true} />
              </SearchBoxDiv>
            )}
            <PositionDivRight desktop={isDesktop}>
              <HeaderRightComponent>
                {!hideInfo && (
                  <>
                    {!user ? (
                      <Link
                        href={{
                          pathname: '/ingresar',
                          query: removeUndefineds({
                            ...router.query,
                            codigo: undefined,
                          }),
                        }}
                        passHref
                      >
                        <LoginButton size="medium">
                          {width >= 900 ? 'Iniciar sesión' : 'Inicia'}
                        </LoginButton>
                      </Link>
                    ) : (
                      <DrawerMenu
                        isFromMenuName={`${user.name?.split(' ')[0]} ${
                          user.lastNames?.[0]?.toLocaleUpperCase() ?? ''
                        }.`}
                      />
                    )}
                  </>
                )}
                {!isDesktop && focusSearchBox.isFocus ? (
                  <CancelButton onClick={handleClose}>Cancelar</CancelButton>
                ) : (
                  <DrawerCart />
                )}
              </HeaderRightComponent>
            </PositionDivRight>
          </HeaderToolbar>
        </HeaderAppBar>
      </RootDiv>
    </>
  );
}
